import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUserAttributes } from '../../../library/shared';
import { updateUserDetailsOnSignIn } from '../../../redux/reducers/userDetails';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../components/snackBar/snakbar';
import { useAsync } from '../../../hooks/useAsync';
import { useRequestEmailChangeOtpMutation, useVerifyEmailChangeOtpMutation } from '../api/emailChangeOtp.api';
import {
  changeEmailRequestSchema,
  changeEmailVerifySchema,
  type ChangeEmailRequestValues,
  type ChangeEmailVerifyValues,
} from '../schemas/changeEmailSchema';
import type { CognitoUserAttributes } from '../types/cognitoUserAttributes';

type ChangeEmailStep = 'request' | 'verify';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }
  return fallback;
}

export function useChangeEmail(currentEmail: string) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState<ChangeEmailStep>('request');
  const [pendingEmail, setPendingEmail] = useState('');
  const [oldEmail, setOldEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  useEffect(() => {
    if (expiresAt == null) {
      setSecondsRemaining(0);
      return undefined;
    }
    const tick = () => setSecondsRemaining(Math.max(expiresAt - Math.floor(Date.now() / 1000), 0));
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [expiresAt]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timerId = setTimeout(() => setResendCooldown((prev) => Math.max(prev - 1, 0)), 1000);
    return () => clearTimeout(timerId);
  }, [resendCooldown]);

  const requestForm = useForm<ChangeEmailRequestValues>({
    resolver: zodResolver(changeEmailRequestSchema),
    defaultValues: { newEmail: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const verifyForm = useForm<ChangeEmailVerifyValues>({
    resolver: zodResolver(changeEmailVerifySchema),
    defaultValues: { oldEmailCode: '', newEmailCode: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const [requestEmailChangeOtp, { isLoading: requestOtpLoading }] = useRequestEmailChangeOtpMutation();
  const [verifyEmailChangeOtp, { isLoading: verifyOtpLoading }] = useVerifyEmailChangeOtpMutation();

  const beginVerification = useCallback(
    async (newEmail: string) => {
      const otpResponse = await requestEmailChangeOtp({ newEmail }).unwrap();

      setPendingEmail(newEmail);
      setOldEmail(otpResponse.oldEmail);
      setExpiresAt(otpResponse.expiresAt);
      setResendCooldown(Math.max(otpResponse.resendAvailableAt - Math.floor(Date.now() / 1000), 0));
      setStep('verify');
      verifyForm.reset({ oldEmailCode: '', newEmailCode: '' });
      showSuccessSnackbar('Verification codes sent to both email addresses');
    },
    [requestEmailChangeOtp, verifyForm]
  );

  const requestEmailChange = useAsync(
    async (values: ChangeEmailRequestValues) => {
      // Clear any stale error from a previous attempt (e.g. a prior "already in use"
      // rejection) before this resubmit's own result comes back.
      requestForm.clearErrors('newEmail');

      if (values.newEmail.trim().toLowerCase() === currentEmail.trim().toLowerCase()) {
        requestForm.setError('newEmail', { type: 'manual', message: 'This is already your current email' });
        return;
      }
      try {
        await beginVerification(values.newEmail.trim());
      } catch (error: unknown) {
        requestForm.setError('newEmail', {
          type: 'manual',
          message: getErrorMessage(error, 'Unable to send verification codes'),
        });
      }
    },
    false
  );

  const verifyEmailChange = useAsync(
    async (values: ChangeEmailVerifyValues) => {
      try {
        await verifyEmailChangeOtp({
          oldEmailCode: values.oldEmailCode.trim(),
          newEmailCode: values.newEmailCode.trim(),
        }).unwrap();
      } catch (error: unknown) {
        const message = getErrorMessage(error, 'Invalid or expired code');
        // Best-effort routing to the field the message is about; a code-expired /
        // too-many-attempts / already-taken message applies to the whole attempt,
        // so surface it on both fields rather than guessing which one to blame.
        if (/current email/i.test(message)) {
          verifyForm.setError('oldEmailCode', { type: 'manual', message });
        } else if (/new email/i.test(message)) {
          verifyForm.setError('newEmailCode', { type: 'manual', message });
        } else {
          verifyForm.setError('oldEmailCode', { type: 'manual', message });
          verifyForm.setError('newEmailCode', { type: 'manual', message });
        }
        return;
      }

      const updatedUserData = (await getUserAttributes()) as CognitoUserAttributes | undefined;
      if (updatedUserData) {
        dispatch(updateUserDetailsOnSignIn(updatedUserData));
      }
      showSuccessSnackbar('Email address updated successfully');
      navigate('/');
    },
    false
  );

  const resendOtp = useAsync(
    async () => {
      if (resendCooldown > 0) return;
      try {
        const otpResponse = await requestEmailChangeOtp({ newEmail: pendingEmail }).unwrap();
        setExpiresAt(otpResponse.expiresAt);
        setResendCooldown(Math.max(otpResponse.resendAvailableAt - Math.floor(Date.now() / 1000), 0));
        verifyForm.reset({ oldEmailCode: '', newEmailCode: '' });
        showSuccessSnackbar('Verification codes resent to both email addresses');
      } catch (error: unknown) {
        showErrorSnackbar(getErrorMessage(error, 'Unable to resend codes'));
      }
    },
    false
  );

  const goBackToRequestStep = useCallback(() => {
    requestForm.setValue('newEmail', pendingEmail);
    verifyForm.reset({ oldEmailCode: '', newEmailCode: '' });
    setResendCooldown(0);
    setExpiresAt(null);
    setStep('request');
  }, [pendingEmail, requestForm, verifyForm]);

  const cancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return {
    step,
    pendingEmail,
    oldEmail,
    secondsRemaining,
    requestForm,
    verifyForm,
    requestLoading: requestEmailChange.loading || requestOtpLoading,
    verifyLoading: verifyEmailChange.loading || verifyOtpLoading,
    resendLoading: resendOtp.loading,
    resendCooldown,
    submitNewEmail: requestEmailChange.run,
    submitVerificationCode: verifyEmailChange.run,
    resendOtp: resendOtp.run,
    goBackToRequestStep,
    cancel,
  };
}
